import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ArkikDialogRef } from '@shared/components/arkik-dialog/arkik-dialog-ref';
import { ArkikDialogService } from '@shared/components/arkik-dialog/arkik-dialog.service';
import { PwaInstallInstructionsComponent } from '@shared/components/pwa-install-instructions/pwa-install-instructions.component';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export interface PwaInstallState {
  platform: 'ios' | 'android' | 'desktop' | 'other';
  flow: 'none' | 'native' | 'ios-instructions';
  runningInstalled: boolean;
  installed: boolean;
  canInstall: boolean;
  installing: boolean;
}

export type PwaInstallResult = 'accepted' | 'dismissed' | 'instructions' | 'unavailable' | 'failed';

@Injectable({ providedIn: 'root' })
export class PwaInstallService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<PwaInstallState>({
    platform: 'other',
    flow: 'none',
    runningInstalled: false,
    installed: false,
    canInstall: false,
    installing: false,
  });
  readonly state$ = this.stateSubject.asObservable();
  private browserWindow: Window | null = null;
  private displayMode: MediaQueryList | null = null;
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private installedThisSession = false;
  private installing = false;
  private initialized = false;
  private destroyed = false;
  private instructionsRef: ArkikDialogRef<PwaInstallInstructionsComponent, void> | null = null;
  private instructionsClosed: Subscription | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    private zone: NgZone,
    private dialog: ArkikDialogService,
  ) {
    this.initialize();
  }

  initialize(): void {
    if (this.initialized || this.destroyed || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.initialized = true;
    this.browserWindow = this.document.defaultView;
    const browserWindow = this.browserWindow;
    if (!browserWindow) {
      return;
    }
    if (typeof browserWindow.matchMedia === 'function') {
      this.displayMode = browserWindow.matchMedia('(display-mode: standalone), (display-mode: tabbed)');
      if (this.displayMode.addEventListener) {
        this.displayMode.addEventListener('change', this.onDisplayChange);
      } else {
        this.displayMode.addListener(this.onDisplayChange);
      }
    }
    browserWindow.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    browserWindow.addEventListener('appinstalled', this.onAppInstalled);
    browserWindow.addEventListener('pageshow', this.onDisplayChange);
    this.document.addEventListener('visibilitychange', this.onDisplayChange);
    this.updateState();
  }

  get runningInstalled(): boolean {
    return this.stateSubject.value.runningInstalled;
  }

  async install(): Promise<PwaInstallResult> {
    this.updateState();
    const state = this.stateSubject.value;
    if (!state.canInstall || this.destroyed) {
      return 'unavailable';
    }
    if (state.flow === 'ios-instructions') {
      if (!this.instructionsRef) {
        this.instructionsRef = this.dialog.open<PwaInstallInstructionsComponent, { safari: boolean }, void>(
          PwaInstallInstructionsComponent,
          {
            width: 'min(480px, calc(100vw - 32px))',
            customClass: ['pwa-install-dialog'],
            enableClose: true,
            data: { safari: this.isIosSafari() },
          },
        );
        this.instructionsClosed = this.instructionsRef.afterClosed.pipe(take(1)).subscribe(() => {
          this.instructionsRef = null;
        });
      }
      return 'instructions';
    }
    const prompt = this.deferredPrompt;
    if (!prompt) {
      return 'unavailable';
    }
    this.deferredPrompt = null;
    this.installing = true;
    this.updateState();
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      return choice.outcome;
    } catch {
      return 'failed';
    } finally {
      this.installing = false;
      this.updateState();
    }
  }

  private readonly onBeforeInstallPrompt = (event: Event): void => {
    event.preventDefault();
    this.zone.run(() => {
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.updateState();
    });
  };

  private readonly onAppInstalled = (): void => {
    this.zone.run(() => {
      this.installedThisSession = true;
      this.deferredPrompt = null;
      this.updateState();
    });
  };

  private readonly onDisplayChange = (): void => {
    this.zone.run(() => this.updateState());
  };

  private updateState(): void {
    if (!this.browserWindow || this.destroyed) {
      return;
    }
    const navigator = this.browserWindow.navigator as Navigator & { standalone?: boolean };
    const runningInstalled = !!this.displayMode?.matches || navigator.standalone === true;
    const installed = runningInstalled || this.installedThisSession;
    if (installed) {
      this.deferredPrompt = null;
      if (!this.stateSubject.value.installed) {
        this.instructionsRef?.close();
      }
    }
    const ios =
      /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const platform: PwaInstallState['platform'] = ios
      ? 'ios'
      : /Android/i.test(navigator.userAgent)
        ? 'android'
        : /Win|Mac|Linux|CrOS/i.test(navigator.platform)
          ? 'desktop'
          : 'other';
    const supportedContext = this.browserWindow.isSecureContext && this.browserWindow.self === this.browserWindow.top;
    const flow: PwaInstallState['flow'] =
      installed || !supportedContext ? 'none' : this.deferredPrompt ? 'native' : ios ? 'ios-instructions' : 'none';
    this.stateSubject.next({
      platform,
      flow,
      runningInstalled,
      installed,
      canInstall: flow !== 'none' && !this.installing,
      installing: this.installing,
    });
  }

  private isIosSafari(): boolean {
    const userAgent = this.browserWindow.navigator.userAgent;
    return /Version\/.*Safari\//.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.browserWindow?.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    this.browserWindow?.removeEventListener('appinstalled', this.onAppInstalled);
    this.browserWindow?.removeEventListener('pageshow', this.onDisplayChange);
    this.document.removeEventListener('visibilitychange', this.onDisplayChange);
    if (this.displayMode?.removeEventListener) {
      this.displayMode.removeEventListener('change', this.onDisplayChange);
    } else {
      this.displayMode?.removeListener(this.onDisplayChange);
    }
    this.instructionsClosed?.unsubscribe();
    this.instructionsRef?.close();
    this.deferredPrompt = null;
    this.stateSubject.complete();
  }
}
